import { Box, Chip, Typography, useTheme } from "@mui/material";
import { QuizOutlined } from "@mui/icons-material";
import { Tables } from "../../database.types";
import { getRoundTitleText } from "../utils/utils";
import StandardQuestionDisplay from "./StandardQuestionDisplay";
import ClassicBonusRoundPlayer from "./ClassicBonusRoundPlayer";
import ClosestGuessBonusRoundPlayer from "./ClosestGuessBonusRoundPlayer";
import Scoreboard from "./Scoreboard";

interface GameContentProps {
  game: Tables<"games">;
  gameState: Tables<"game_state">;
  rounds: Tables<"rounds">[];
  questions: Tables<"questions">[];
  lifelines: Tables<"round_lifelines">[];
  bonusRounds: Tables<"bonus_rounds">[];
  classicBonusAnswers: Tables<"classic_bonus_answers">[];
  classicBonusTeamResponses: Tables<"classic_bonus_team_responses">[];
  closestGuessQuestions: Tables<"closest_guess_questions">[];
  team1: Tables<"teams">;
  team2: Tables<"teams">;
  team1LogoUrl: string | null;
  team2LogoUrl: string | null;
}

const GameContent = ({
  game,
  gameState,
  rounds,
  questions,
  lifelines,
  bonusRounds,
  classicBonusAnswers,
  classicBonusTeamResponses,
  closestGuessQuestions,
  team1,
  team2,
  team1LogoUrl,
  team2LogoUrl,
}: GameContentProps) => {
  const theme = useTheme();

  const mainBonus = bonusRounds.find((br) => br.phase === "bonus");
  const overtime = bonusRounds.find((br) => br.phase === "overtime");

  // for turn indicators
  let isQuestion1 = gameState.current_question_num === 1;

  if (gameState.current_phase === "bonus" && mainBonus?.type === "Classic (List)") {
    const team1Responses = classicBonusTeamResponses.filter(
      (r) => r.bonus_round_id === mainBonus.id && r.team_id === team1.id,
    );
    const team2Responses = classicBonusTeamResponses.filter(
      (r) => r.bonus_round_id === mainBonus.id && r.team_id === team2.id,
    );
    isQuestion1 = team1Responses.length === team2Responses.length;
  }
  if (gameState.current_phase === "overtime" && overtime?.type === "Classic (List)") {
    const team1Responses = classicBonusTeamResponses.filter(
      (r) => r.bonus_round_id === overtime.id && r.team_id === team1.id,
    );
    const team2Responses = classicBonusTeamResponses.filter(
      (r) => r.bonus_round_id === overtime.id && r.team_id === team2.id,
    );
    isQuestion1 = team1Responses.length === team2Responses.length;
  }

  const getCurrentStandardQuestion = () => {
    if (gameState.current_phase !== "standard" || !gameState.current_round_number) {
      return null;
    }
    const currentRound = rounds.find(
      (r) => r.round_number === gameState.current_round_number,
    );
    if (!currentRound) return null;
    return (
      questions.find(
        (q) =>
          q.round_id === currentRound.id &&
          q.question_number === gameState.current_question_num,
      ) ?? null
    );
  };

  // page body means question (including answer), bonus round display, or any placeholder panel
  const renderPageBody = () => {
    /* Standard Round Display */
    if (gameState.current_phase === "standard" && gameState.current_round_number) {
      const questionObj = getCurrentStandardQuestion();
      if (questionObj) {
        return (
          <StandardQuestionDisplay
            question={questionObj.question_text}
            answer={questionObj.answer_text}
            questionId={questionObj.id}
            showAnswer={gameState.show_answer}
            showQuestion={gameState.show_question}
          />
        );
      }
      return <Typography variant="h3">Error</Typography>;
    }

    /* Classic Bonus Round */
    if (gameState.current_phase === "bonus" && mainBonus?.type === "Classic (List)") {
      const answers = classicBonusAnswers.filter(
        (a) => a.bonus_round_id === mainBonus.id,
      );
      const responses = classicBonusTeamResponses.filter(
        (r) => r.bonus_round_id === mainBonus.id,
      );
      return (
        <ClassicBonusRoundPlayer
          bonusRound={mainBonus}
          classicAnswers={answers}
          teamResponses={responses}
          team1Id={team1.id}
          team2Id={team2.id}
          team1Name={team1.name}
          team2Name={team2.name}
        />
      );
    }

    /* Classic Overtime */
    if (gameState.current_phase === "overtime" && overtime?.type === "Classic (List)") {
      const answers = classicBonusAnswers.filter(
        (a) => a.bonus_round_id === overtime.id,
      );
      const responses = classicBonusTeamResponses.filter(
        (r) => r.bonus_round_id === overtime.id,
      );
      return (
        <ClassicBonusRoundPlayer
          bonusRound={overtime}
          classicAnswers={answers}
          teamResponses={responses}
          team1Id={team1.id}
          team2Id={team2.id}
          team1Name={team1.name}
          team2Name={team2.name}
        />
      );
    }

    /* Closest Guess Bonus Round */
    if (
      gameState.current_phase === "bonus" &&
      mainBonus?.type === "Closest Guess Battle"
    ) {
      const cgqs = closestGuessQuestions.filter(
        (q) => q.bonus_round_id === mainBonus.id,
      );
      return (
        <ClosestGuessBonusRoundPlayer
          bonusRound={mainBonus}
          closestGuessQuestions={cgqs}
          team1Name={team1.name}
          team2Name={team2.name}
        />
      );
    }

    /* Closest Guess Overtime */
    if (
      gameState.current_phase === "overtime" &&
      overtime?.type === "Closest Guess Battle"
    ) {
      const cgqs = closestGuessQuestions.filter(
        (q) => q.bonus_round_id === overtime.id,
      );
      return (
        <ClosestGuessBonusRoundPlayer
          bonusRound={overtime}
          closestGuessQuestions={cgqs}
          team1Name={team1.name}
          team2Name={team2.name}
        />
      );
    }

    return null;
  };

  const category = (() => {
    if (gameState.current_round_number === 10) {
      return gameState.current_question_num === 1
        ? game.team_1_niche_category
        : game.team_2_niche_category;
    }
    const currentRound = rounds.find(
      (r) => r.round_number === gameState.current_round_number,
    );
    return currentRound?.category ?? undefined;
  })();

  const roundIdx = (() => {
    if (gameState.current_phase === "standard" && gameState.current_round_number) {
      return gameState.current_round_number - 1; // convert to 0-indexed for display function
    }
    if (gameState.current_phase === "bonus") return "bonus" as const;
    if (gameState.current_phase === "overtime") return "overtime" as const;
    return null;
  })();

  const roundTitleText = getRoundTitleText(roundIdx, category ?? undefined);

  return (
    <>
      {/* Round Title */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "32px",
          mb: 1,
        }}
      >
        <Chip
          label={!isQuestion1 && gameState.is_steal ? "STEAL" : "TURN"}
          variant="outlined"
          sx={{
            py: 1,
            px: { xs: 4, sm: 6 },
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            fontWeight: "bold",
            height: "auto",
            "& .MuiChip-icon": {
              ml: { xs: 0.5, sm: 1 },
              color:
                isQuestion1 || gameState.is_steal
                  ? theme.palette.primary.light
                  : theme.palette.primary.dark,
            },
            color:
              isQuestion1 || gameState.is_steal
                ? theme.palette.primary.light
                : theme.palette.primary.dark,
            border: `1px solid ${
              isQuestion1 || gameState.is_steal
                ? theme.palette.primary.light
                : theme.palette.primary.dark
            }`,
          }}
        />
        <Chip
          icon={<QuizOutlined />}
          label={roundTitleText}
          sx={{
            py: 1,
            px: { xs: 1, sm: 2 },
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            fontWeight: "bold",
            height: "auto",
            "& .MuiChip-icon": {
              ml: { xs: 0.5, sm: 1 },
              color: "white",
            },
            color: "white",
          }}
        />
        <Chip
          label={isQuestion1 && gameState.is_steal ? "STEAL" : "TURN"}
          variant="outlined"
          sx={{
            py: 1,
            px: { xs: 4, sm: 6 },
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            fontWeight: "bold",
            height: "auto",
            "& .MuiChip-icon": {
              ml: { xs: 0.5, sm: 1 },
              color:
                !isQuestion1 || gameState.is_steal
                  ? theme.palette.primary.light
                  : theme.palette.primary.dark,
            },
            color:
              !isQuestion1 || gameState.is_steal
                ? theme.palette.primary.light
                : theme.palette.primary.dark,
            border: `1px solid ${
              !isQuestion1 || gameState.is_steal
                ? theme.palette.primary.light
                : theme.palette.primary.dark
            }`,
          }}
        />
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Game Question/Content Area */}
        <Box
          sx={{
            flex: "1 1 auto",
            mb: { xs: 1, sm: 2 },
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            width: "100%",
          }}
        >
          {renderPageBody()}
        </Box>

        {/* Team Scores - Full Width Bottom Bar */}
        <Scoreboard
          team1={team1}
          team2={team2}
          gameState={gameState}
          rounds={rounds}
          questions={questions}
          lifelines={lifelines}
          bonusRounds={bonusRounds}
          classicBonusAnswers={classicBonusAnswers}
          classicBonusTeamResponses={classicBonusTeamResponses}
          closestGuessQuestions={closestGuessQuestions}
          team1LogoUrl={team1LogoUrl}
          team2LogoUrl={team2LogoUrl}
        />
      </Box>
    </>
  );
};

export default GameContent;
